import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useUploadStore } from "../stores/videoUploadStore";

// Fix for default marker icon not showing
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerIconShadow from "leaflet/dist/images/marker-shadow.png";

const defaultIcon = L.icon({
  iconUrl: markerIcon,
  shadowUrl: markerIconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const highConfidenceIcon = L.icon({
  iconUrl:
    "https://cdn.rawgit.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-yellow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Use red for defects
const defectIcon = L.icon({
  iconUrl:
    "https://cdn.rawgit.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Use yellow for traffic signs
const signIcon = L.icon({
  iconUrl:
    "https://cdn.rawgit.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-yellow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const defectList = [
  "Potholes",
  "Raveling road",
  "Alligator Cracks",
  "Block Cracks",
  "Longitudinal Cracks",
  "Faulting",
  "Block cracks",
  "Transverse cracks",
  "Edge cracks",
  "Reflection Cracks",
  "Rutting",
  "Edge Cracks",
];

const signList = [
  "GIVE WAY SIGN 302",
  "AHEAD ONLY TURN ONLY SIGN 322",
  "ROUNDABOUT SIGN 329",
  // ... rest of sign list remains unchanged
];

const isDefect = (label) => {
  return defectList.some(
    (defect) => defect.trim().toLowerCase() === label.trim().toLowerCase()
  );
};

const isSign = (label) => {
  return signList.some(
    (sign) => sign.trim().toLowerCase() === label.trim().toLowerCase()
  );
};

// Helper function to convert date string to timestamp in milliseconds
const dateToTimestamp = (dateString) => {
  return new Date(dateString).getTime();
};

// Generate a random color for each segment's polyline
const getRandomColor = () => {
  const colors = [
    "#3388ff",
    "#ff6b6b",
    "#8c54ff",
    "#5cb85c",
    "#f0ad4e",
    "#46b8da",
  ];
  return colors[Math.floor(Math.random() * colors.length)];
};

const MapComponent = ({
  surveyId,
  selectedFilter,
  mapData,
  isDashboard = false,
}) => {
  const [gpxData, setGpxData] = useState([]);
  const [detectedObjects, setDetectedObjects] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [currentLocationMarker, setCurrentLocationMarker] = useState(null);
  const [isLocating, setIsLocating] = useState(false);
  const { fetchVideoData, data } = useUploadStore();

  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);
  const polylinesRef = useRef([]);

  // Initialize map
  useEffect(() => {
    if (!mapInstance.current) {
      mapInstance.current = L.map(mapRef.current).setView([51.505, -0.09], 13);

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }).addTo(mapInstance.current);
    }
  }, []);

  // Handle dashboard mode for multiple segments
  useEffect(() => {
    if (isDashboard && mapInstance.current && mapData?.mapSegments) {
      // Clear previous markers and polylines
      markersRef.current.forEach((marker) => marker.remove());
      polylinesRef.current.forEach((polyline) => polyline.remove());
      markersRef.current = [];
      polylinesRef.current = [];

      // Array to store all coordinates for fitting bounds
      let allCoordinates = [];

      // Process each segment
      mapData.mapSegments.forEach((segment, index) => {
        if (segment?.gpxData && segment.gpxData.length > 0) {
          // Process GPX points to include timestamps
          const processedGpxData = segment.gpxData.map((point) => ({
            ...point,
            timestamp_ms: dateToTimestamp(point.time),
          }));

          // Create polyline coordinates
          const coordinates = processedGpxData.map((point) => [
            point.lat,
            point.lon,
          ]);

          // Add coordinates to our collection for map bounds
          allCoordinates = [...allCoordinates, ...coordinates];

          // Create a polyline with a unique color for this segment
          const segmentColor = getRandomColor();
          const polyline = L.polyline(coordinates, {
            color: segmentColor,
            weight: 3,
            opacity: 0.7,
            smoothFactor: 1,
          })
            .addTo(mapInstance.current)
            .bindPopup(
              `<div><strong>${
                segment.roadName || "Unnamed Road"
              }</strong></div>`
            );

          polylinesRef.current.push(polyline);

          // If there are detected objects for this segment, add them to the map
          if (segment.detectedObjects && segment.detectedObjects.length > 0) {
            // Get the video start time (first GPX point time)
            const videoStartTime = processedGpxData[0].timestamp_ms;

            segment.detectedObjects.forEach((obj, objIndex) => {
              // Validate object
              if (!obj || !obj.label || !obj.timestamp_ms) {
                console.warn(
                  `Invalid object in segment ${index}, index ${objIndex}:`,
                  obj
                );
                return;
              }
              // Skip objects that don't match the filter if a filter is selected
              if (selectedFilter === "defects" && !isDefect(obj.label)) return;
              if (selectedFilter === "signs" && !isSign(obj.label)) return;

              // Find the closest GPX point by timestamp
              const objectAbsoluteTime = videoStartTime + obj.timestamp_ms;
              const closestPoint = processedGpxData.reduce((prev, curr) => {
                const prevTimeDiff = Math.abs(
                  prev.timestamp_ms - objectAbsoluteTime
                );
                const currTimeDiff = Math.abs(
                  curr.timestamp_ms - objectAbsoluteTime
                );
                return currTimeDiff < prevTimeDiff ? curr : prev;
              });

              // Determine which icon to use based on the object type
              let icon;
              if (isSign(obj.label)) {
                icon = signIcon;
              } else if (isDefect(obj.label)) {
                icon = defectIcon;
              } else if (obj.confidence > 0.5) {
                icon = highConfidenceIcon;
              } else {
                icon = defaultIcon;
              }

              // Create marker
              const marker = L.marker([closestPoint.lat, closestPoint.lon], {
                icon,
              })
                .addTo(mapInstance.current)
                .bindPopup(
                  `<div>
                    <strong>${obj.label}</strong><br>
                    <strong>Road: ${
                      segment.roadName || "Unnamed Road"
                    }</strong><br>
                    Confidence: ${(obj.confidence * 100).toFixed(2)}%<br>
                    Object Timestamp: ${obj.timestamp_ms}ms<br>
                    GPS Time: ${new Date(
                      closestPoint.time
                    ).toLocaleTimeString()}<br>
                    Time Difference: ${(
                      Math.abs(closestPoint.timestamp_ms - objectAbsoluteTime) /
                      1000
                    ).toFixed(2)}s<br>
                    ${obj.depth ? `Depth: ${obj.depth}` : ""}
                  </div>`
                );

              markersRef.current.push(marker);
            });
          } else {
            console.log(`No detectedObjects in segment ${index}`);
          }
        }
      });

      // Fit map bounds to include all coordinates
      if (allCoordinates.length > 0) {
        mapInstance.current.fitBounds(allCoordinates);
      }
    }

    // Cleanup function
    return () => {
      if (markersRef.current.length > 0) {
        markersRef.current.forEach((marker) => {
          if (marker && mapInstance.current) {
            marker.remove();
          }
        });
        markersRef.current = [];
      }

      if (polylinesRef.current.length > 0) {
        polylinesRef.current.forEach((polyline) => {
          if (polyline && mapInstance.current) {
            polyline.remove();
          }
        });
        polylinesRef.current = [];
      }
    };
  }, [mapData, isDashboard, selectedFilter]);

  // Single survey handling (non-dashboard mode)
  useEffect(() => {
    if (!isDashboard) {
      fetchVideoData(surveyId);
    }
  }, [fetchVideoData, surveyId, isDashboard]);

  console.log("Survey ID:", surveyId);
  console.log("Data from store:", data);

  useEffect(() => {
    if (!isDashboard && data && data?.length > 0) {
      const gpxDataFromVideo = data[0]?.gpxData;
      if (gpxDataFromVideo && Array.isArray(gpxDataFromVideo)) {
        setGpxData(gpxDataFromVideo);
      }

      const detectedObjectsFromVideo = data[0]?.detectedObjects;
      if (detectedObjectsFromVideo && Array.isArray(detectedObjectsFromVideo)) {
        setDetectedObjects(detectedObjectsFromVideo);
      }
    }
  }, [data, isDashboard]);

  // Function to get current user location
  const getCurrentLocation = () => {
    setIsLocating(true);
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setCurrentLocation([latitude, longitude]);
          setIsLocating(false);
        },
        (error) => {
          console.error("Error getting location:", error);
          alert(
            "Unable to get your location. Please check your location permissions."
          );
          setIsLocating(false);
        },
        { enableHighAccuracy: true }
      );
    } else {
      alert("Geolocation is not supported by your browser");
      setIsLocating(false);
    }
  };

  // Update map when current location changes
  useEffect(() => {
    if (currentLocation && mapInstance.current) {
      const [lat, lng] = currentLocation;

      // Remove previous marker if it exists
      if (currentLocationMarker) {
        currentLocationMarker.remove();
      }

      // Add new marker
      const marker = L.marker([lat, lng], {
        icon: L.icon({
          iconUrl:
            "https://cdn.rawgit.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png",
          iconSize: [30, 45],
          iconAnchor: [15, 45],
          popupAnchor: [1, -34],
          shadowSize: [41, 41],
        }),
      })
        .addTo(mapInstance.current)
        .bindPopup("Your current location")
        .openPopup();

      setCurrentLocationMarker(marker);
      mapInstance.current.setView([lat, lng], 15);
    }
  }, [currentLocation]);

  // Handle single survey GPX data
  useEffect(() => {
    if (!isDashboard && gpxData.length > 0) {
      // Clear existing polylines
      polylinesRef.current.forEach((polyline) => polyline.remove());
      polylinesRef.current = [];

      const coordinates = gpxData.map((point) => [point.lat, point.lon]);
      const polyline = L.polyline(coordinates, {
        color: "blue",
        weight: 6,
      }).addTo(mapInstance.current);
      polylinesRef.current.push(polyline);
      mapInstance.current.fitBounds(coordinates);
    }
  }, [gpxData, isDashboard]);

  // Handle single survey detected objects
  useEffect(() => {
    if (!isDashboard) {
      // Clear existing markers
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];

      if (detectedObjects && gpxData.length > 0) {
        // Process GPS data to include timestamps in milliseconds
        const processedGpxData = gpxData.map((point) => ({
          ...point,
          timestamp_ms: dateToTimestamp(point.time),
        }));

        // Get the video start time (first GPX point time)
        const videoStartTime = processedGpxData[0].timestamp_ms;

        detectedObjects.forEach((obj) => {
          // Skip objects that don't match the filter
          if (selectedFilter === "defects" && !isDefect(obj.label)) return;
          if (selectedFilter === "signs" && !isSign(obj.label)) return;

          // Find the closest GPX point by timestamp
          const objectAbsoluteTime = videoStartTime + obj.timestamp_ms;
          const closestPoint = processedGpxData.reduce((prev, curr) => {
            const prevTimeDiff = Math.abs(
              prev.timestamp_ms - objectAbsoluteTime
            );
            const currTimeDiff = Math.abs(
              curr.timestamp_ms - objectAbsoluteTime
            );
            return currTimeDiff < prevTimeDiff ? curr : prev;
          });

          // Determine which icon to use based on the object type
          let icon;
          if (isSign(obj.label)) {
            icon = signIcon;
          } else if (isDefect(obj.label)) {
            icon = defectIcon;
          } else if (obj.confidence > 0.5) {
            icon = highConfidenceIcon;
          } else {
            icon = defaultIcon;
          }

          const marker = L.marker([closestPoint.lat, closestPoint.lon], {
            icon,
          })
            .addTo(mapInstance.current)
            .bindPopup(
              `<div>
                <strong>${obj.label}</strong><br>
                Confidence: ${(obj.confidence * 100).toFixed(2)}%<br>
                Object Timestamp: ${obj.timestamp_ms}ms<br>
                GPS Time: ${new Date(
                  closestPoint.time
                ).toLocaleTimeString()}<br>
                Time Difference: ${(
                  Math.abs(closestPoint.timestamp_ms - objectAbsoluteTime) /
                  1000
                ).toFixed(2)}s<br>
                Depth: ${obj.depth}
              </div>`
            );

          markersRef.current.push(marker);
        });
      }
    }
  }, [detectedObjects, gpxData, selectedFilter, isDashboard]);

  return (
    <div style={{ position: "relative" }}>
      <div
        ref={mapRef}
        style={{ height: "400px", width: "100%", margin: "20px 0" }}
      ></div>
      <button
        onClick={getCurrentLocation}
        disabled={isLocating}
        style={{
          position: "absolute",
          top: "30px",
          right: "10px",
          zIndex: 1000,
          padding: "8px 12px",
          backgroundColor: "#007bff",
          color: "white",
          border: "none",
          borderRadius: "4px",
          cursor: isLocating ? "default" : "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isLocating ? (
          "Locating..."
        ) : (
          <>
            <span style={{ marginRight: "5px" }}>📍</span>
            My Location
          </>
        )}
      </button>
    </div>
  );
};

export default MapComponent;
